import React, { useState } from "react";
import styled from "styled-components";
import { Divider, UI_CAPTION, UI_CAPTION_SEMIBOLD, colors2, icons } from "oolib";
import {
  PanelItem,
  StyledScrollableWrapper,
  StyledHierarchyItem,
} from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/comps/PanelItem";
import { ListItem } from "@/components/UI/List/comps/ListItem";
import { Button } from "@/components/UI/CompactControls";
import { useGetCohorts, useDeleteCohort } from "@/utils/react-query-hooks/cohorts";

const { Trash } = icons;

// Row body applies the cohort; the trash handle (hover-revealed) deletes it.
// Same body-vs-handle split as PanelItem's selectable-folder variant.
const StyledCohortRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  .cohort-delete { opacity: 0; }
  &:hover .cohort-delete, &:focus-within .cohort-delete { opacity: 1; }
`;

const StyledDeleteHandle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 0 1rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.4rem;
  &:hover { background-color: ${colors2.grey10}; }
`;

const StyledConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1rem;
  white-space: nowrap;
  /* The panel has a fixed width; a long cohort name truncates instead of
     pushing the Delete / Cancel buttons out of view. */
  > :first-child {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CohortRow = ({ cohort, setActiveFilters }) => {
  const [confirming, setConfirming] = useState(false);
  const { mutate: deleteCohort, isLoading } = useDeleteCohort();

  if (confirming) {
    return (
      <StyledConfirmRow onClick={(e) => e.stopPropagation()}>
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey80 }}>
          Delete “{cohort.name}”?
        </UI_CAPTION_SEMIBOLD>
        <Button onClick={() => deleteCohort({ cohortId: cohort._id })} disabled={isLoading} style={{ color: colors2.red }}>
          {isLoading ? "Deleting…" : "Delete"}
        </Button>
        <Button onClick={() => setConfirming(false)} disabled={isLoading}>Cancel</Button>
      </StyledConfirmRow>
    );
  }

  return (
    <StyledCohortRow>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListItem
          S
          isMulti={false}
          option={{ _id: cohort._id, display: cohort.name, icon: "Users" }}
          onChange={() => setActiveFilters(cohort.activeFilters)}
          style={{ width: "100%" }}
        />
      </div>
      <StyledDeleteHandle
        className="cohort-delete"
        type="button"
        aria-label={`Delete cohort ${cohort.name}`}
        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
      >
        <Trash size={14} color={colors2.grey60} />
      </StyledDeleteHandle>
    </StyledCohortRow>
  );
};

const CohortsPanel = ({ setActiveFilters }) => {
  const { data: cohorts = [], isLoading } = useGetCohorts();

  if (!isLoading && cohorts.length === 0) {
    return (
      <UI_CAPTION style={{ padding: "0.8rem 1.2rem", color: colors2.grey50, whiteSpace: "nowrap" }}>
        No cohorts saved yet
      </UI_CAPTION>
    );
  }

  return (
    <StyledScrollableWrapper style={{ width: "28rem" }}>
      {isLoading
        ? [1, 2, 3].map((i) => <ListItem key={i} option={{ loading: true }} S />)
        : cohorts.map((cohort) => (
            <CohortRow key={cohort._id} cohort={cohort} setActiveFilters={setActiveFilters} />
          ))}
    </StyledScrollableWrapper>
  );
};

/**
 * Bottom section of the Add Filter lightbox: a "Cohorts" drilldown listing
 * saved filter combinations. Picking one replaces the active filters with
 * the cohort's saved set; hovering a row reveals its delete handle.
 */
export const CohortsSection = ({ setActiveFilters }) => (
  <>
    <div data-lightbox-divider><Divider style={{ margin: "0.4rem 0" }} /></div>
    {/* StyledHierarchyItem stretches the LightboxTrigger span so the row is full width */}
    <StyledHierarchyItem>
      <PanelItem option={{ display: "Cohorts", icon: "Users", hasChildren: true }}>
        <CohortsPanel setActiveFilters={setActiveFilters} />
      </PanelItem>
    </StyledHierarchyItem>
  </>
);
